import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const RECOVERABLE_IMPORT_ERROR = /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
const MAX_AUTO_RELOADS = 2;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryKey: number;
  autoReloading: boolean;
}

export class LazyErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, retryKey: 0, autoReloading: false };

  private isRecoverableImportError(error: Error | null) {
    if (!error) return false;
    return RECOVERABLE_IMPORT_ERROR.test(error.message);
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    if (typeof window === 'undefined') return;
    if (!this.isRecoverableImportError(error)) return;

    const url = new URL(window.location.href);
    const retryCount = Number(url.searchParams.get('__lazy_retry') ?? '0');
    if (retryCount >= MAX_AUTO_RELOADS) return;

    // Auto hard-reload once to bust stale chunk caches — no manual screen needed.
    this.setState({ autoReloading: true });
    url.searchParams.set('__lazy_retry', String(retryCount + 1));
    url.searchParams.set('__retry', Date.now().toString());
    window.location.replace(url.toString());
  }

  handleRetry = () => {
    if (typeof window !== 'undefined' && this.isRecoverableImportError(this.state.error)) {
      window.location.reload();
      return;
    }

    this.setState((state) => ({
      hasError: false,
      error: null,
      retryKey: state.retryKey + 1,
      autoReloading: false,
    }));
  };

  render() {
    if (this.state.hasError) {
      // While auto-reloading, render nothing to avoid a flash of the error screen.
      if (this.state.autoReloading) return null;

      const isRecoverableImportError = this.isRecoverableImportError(this.state.error);

      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>
          <h2 className="text-lg font-semibold">Failed to load this page</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {isRecoverableImportError
              ? 'A temporary loading error occurred. Refresh to sync the latest files and try again.'
              : 'An unexpected error occurred. Click retry to try again.'}
          </p>
          <Button onClick={this.handleRetry} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {isRecoverableImportError ? 'Refresh page' : 'Retry'}
          </Button>
        </div>
      );
    }

    return <div key={this.state.retryKey}>{this.props.children}</div>;
  }
}

